import React, { useState, useEffect, useCallback, useRef } from 'react';
import { fetchMonitorIstekler, fetchMonitorOzet } from '../services/apiMonitorService';
import './MonitorPage.css';

/**
 * AP > Sistem > Monitör
 * API'ye gelen isteklerin logu (yalnızca admin). Hassas alanlar backend'de maskelenir.
 */

const BOS_FILTRE = {
  baslangic: '',
  bitis: '',
  method: '',
  durum: '',
  path: '',
  ip: '',
  kullanici: '',
  rol: '',
};

const LIMIT = 50;

const hataMesaji = (error, fallback) => {
  if (!error) return fallback;
  if (error?.status === 401) return 'Oturum süresi dolmuş olabilir; tekrar giriş yapın.';
  if (error?.status === 403) return 'Bu sayfa yalnızca yöneticiler içindir.';
  return error?.message || error?.data?.mesaj || fallback;
};

const zamanFormat = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

const yerelTarihtenIso = (v) => {
  if (!v) return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString();
};

const durumSinifi = (kod) => {
  if (kod == null) return 'mon-durum--yok';
  if (kod >= 500) return 'mon-durum--5xx';
  if (kod >= 400) return 'mon-durum--4xx';
  if (kod >= 300) return 'mon-durum--3xx';
  return 'mon-durum--2xx';
};

const jsonGoster = (v) => {
  if (v == null) return '—';
  if (typeof v === 'string') return v;
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
};

const MonitorPage = () => {
  const [filtre, setFiltre] = useState(BOS_FILTRE);
  const [uygulanan, setUygulanan] = useState(BOS_FILTRE);
  const [sayfa, setSayfa] = useState(1);
  const [kayitlar, setKayitlar] = useState([]);
  const [toplam, setToplam] = useState(null);
  const [durum, setDurum] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [acikId, setAcikId] = useState(null);
  const [otoYenile, setOtoYenile] = useState(false);
  const [sonYenileme, setSonYenileme] = useState(null);

  const [ozetSaat, setOzetSaat] = useState(24);
  const [ozet, setOzet] = useState(null);
  const [ozetLoading, setOzetLoading] = useState(true);

  const istekSirasi = useRef(0);

  const listeYukle = useCallback(
    async (sessiz = false) => {
      const sira = ++istekSirasi.current;
      if (!sessiz) setLoading(true);
      setError('');
      try {
        const res = await fetchMonitorIstekler({
          ...uygulanan,
          baslangic: yerelTarihtenIso(uygulanan.baslangic),
          bitis: yerelTarihtenIso(uygulanan.bitis),
          sayfa,
          limit: LIMIT,
        });
        if (sira !== istekSirasi.current) return;
        const veri = res?.veri || {};
        setKayitlar(Array.isArray(veri.kayitlar) ? veri.kayitlar : []);
        setToplam(typeof veri.toplam === 'number' ? veri.toplam : null);
        setDurum(veri.durum || null);
        setSonYenileme(new Date());
      } catch (err) {
        if (sira !== istekSirasi.current) return;
        setError(hataMesaji(err, 'Loglar yüklenemedi'));
        if (!sessiz) setKayitlar([]);
      } finally {
        if (sira === istekSirasi.current) setLoading(false);
      }
    },
    [uygulanan, sayfa]
  );

  const ozetYukle = useCallback(async () => {
    setOzetLoading(true);
    try {
      const res = await fetchMonitorOzet(ozetSaat);
      setOzet(res?.veri || null);
    } catch {
      setOzet(null);
    } finally {
      setOzetLoading(false);
    }
  }, [ozetSaat]);

  useEffect(() => {
    listeYukle();
  }, [listeYukle]);

  useEffect(() => {
    ozetYukle();
  }, [ozetYukle]);

  useEffect(() => {
    if (!otoYenile) return undefined;
    const t = setInterval(() => listeYukle(true), 10000);
    return () => clearInterval(t);
  }, [otoYenile, listeYukle]);

  const alanDegis = (e) => {
    const { name, value } = e.target;
    setFiltre((f) => ({ ...f, [name]: value }));
  };

  const filtrele = (e) => {
    if (e) e.preventDefault();
    setSayfa(1);
    setAcikId(null);
    setUygulanan({ ...filtre });
  };

  const temizle = () => {
    setFiltre(BOS_FILTRE);
    setUygulanan(BOS_FILTRE);
    setSayfa(1);
    setAcikId(null);
  };

  const ipFiltrele = (ip) => {
    if (!ip) return;
    const yeni = { ...BOS_FILTRE, ip };
    setFiltre(yeni);
    setUygulanan(yeni);
    setSayfa(1);
    setAcikId(null);
  };

  const yenile = () => {
    listeYukle();
    ozetYukle();
  };

  const toplamSayfa = toplam != null ? Math.max(1, Math.ceil(toplam / LIMIT)) : null;
  const sonrakiVar = toplamSayfa != null ? sayfa < toplamSayfa : kayitlar.length === LIMIT;
  const s = ozet?.siniflar;

  return (
    <div className="page-container mon-wrap">
      <div className="page-header mon-header">
        <div>
          <h1>Monitör</h1>
          <p className="mon-alt">
            API'ye gelen istekler. Şifre, token, OTP, kart, IBAN ve TC alanları maskelenir; telefonların yalnızca son 4
            hanesi görünür. En eski kayıtlar depolama sınırı dolunca otomatik silinir.
          </p>
        </div>
        <div className="mon-header-aksiyon">
          <label className="mon-oto">
            <input type="checkbox" checked={otoYenile} onChange={(e) => setOtoYenile(e.target.checked)} />
            Otomatik yenile (10 sn)
          </label>
          <button type="button" className="mon-btn mon-btn--primary" onClick={yenile} disabled={loading}>
            <i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''}`} aria-hidden /> Yenile
          </button>
        </div>
      </div>

      {durum && !durum.etkin && (
        <div className="mon-alert mon-alert--warn" role="status">
          İstek kaydı sunucuda kapalı (API_MONITOR_ENABLED=false). Yeni istekler kaydedilmiyor.
        </div>
      )}

      {/* Özet */}
      <section className="mon-panel">
        <div className="mon-panel-head">
          <h2>
            <i className="fas fa-shield-alt" aria-hidden /> Özet
          </h2>
          <select
            className="mon-input mon-input--sm"
            value={ozetSaat}
            onChange={(e) => setOzetSaat(parseInt(e.target.value, 10))}
            aria-label="Özet aralığı"
          >
            <option value={1}>Son 1 saat</option>
            <option value={24}>Son 24 saat</option>
            <option value={168}>Son 7 gün</option>
          </select>
        </div>
        {ozetLoading && !ozet ? (
          <div className="mon-bos">Yükleniyor…</div>
        ) : !ozet ? (
          <div className="mon-bos">Özet alınamadı.</div>
        ) : (
          <>
            <div className="mon-kartlar">
              <div className="mon-kart">
                <span>Toplam istek</span>
                <strong>{s.toplam.toLocaleString('tr-TR')}</strong>
              </div>
              <div className="mon-kart mon-kart--2xx">
                <span>Başarılı (2xx)</span>
                <strong>{s['2xx'].toLocaleString('tr-TR')}</strong>
              </div>
              <div className="mon-kart mon-kart--4xx">
                <span>İstemci hatası (4xx)</span>
                <strong>{s['4xx'].toLocaleString('tr-TR')}</strong>
              </div>
              <div className="mon-kart mon-kart--5xx">
                <span>Sunucu hatası (5xx)</span>
                <strong>{s['5xx'].toLocaleString('tr-TR')}</strong>
              </div>
            </div>

            <div className="mon-ozet-grid">
              <div>
                <h3>En çok 4xx alan IP'ler</h3>
                <p className="mon-ipucu">401/403/404/429 yığılması yoklama veya kaba kuvvet denemesi olabilir.</p>
                {ozet.supheliIpler.length === 0 ? (
                  <div className="mon-bos mon-bos--sm">Kayıt yok.</div>
                ) : (
                  <div className="mon-tablo-kap">
                    <table className="mon-tablo mon-tablo--sm">
                      <thead>
                        <tr>
                          <th>IP</th>
                          <th>Adet</th>
                          <th>Kodlar</th>
                          <th>Örnek yollar</th>
                          <th>Son</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ozet.supheliIpler.map((r) => (
                          <tr key={r.ip || 'yok'}>
                            <td>
                              <button type="button" className="mon-link" onClick={() => ipFiltrele(r.ip)}>
                                {r.ip || '—'}
                              </button>
                            </td>
                            <td>{r.adet}</td>
                            <td>{(r.kodlar || []).sort().join(', ')}</td>
                            <td className="mon-yollar">{(r.ornekYollar || []).join('  ·  ')}</td>
                            <td>{zamanFormat(r.sonZaman)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              <div>
                <h3>En çok istek atan IP'ler</h3>
                <p className="mon-ipucu">Kendi ofis/sunucu IP'lerinizi tanıyın; yabancı bir IP'nin yoğunluğu dikkat çekmeli.</p>
                {ozet.enCokIstek.length === 0 ? (
                  <div className="mon-bos mon-bos--sm">Kayıt yok.</div>
                ) : (
                  <div className="mon-tablo-kap">
                    <table className="mon-tablo mon-tablo--sm">
                      <thead>
                        <tr>
                          <th>IP</th>
                          <th>Adet</th>
                          <th>Son</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ozet.enCokIstek.map((r) => (
                          <tr key={r.ip || 'yok'}>
                            <td>
                              <button type="button" className="mon-link" onClick={() => ipFiltrele(r.ip)}>
                                {r.ip || '—'}
                              </button>
                            </td>
                            <td>{r.adet.toLocaleString('tr-TR')}</td>
                            <td>{zamanFormat(r.sonZaman)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </section>

      {/* Filtre */}
      <form className="mon-panel mon-filtre" onSubmit={filtrele}>
        <div className="mon-filtre-grid">
          <label>
            Başlangıç
            <input type="datetime-local" name="baslangic" className="mon-input" value={filtre.baslangic} onChange={alanDegis} />
          </label>
          <label>
            Bitiş
            <input type="datetime-local" name="bitis" className="mon-input" value={filtre.bitis} onChange={alanDegis} />
          </label>
          <label>
            Method
            <select name="method" className="mon-input" value={filtre.method} onChange={alanDegis}>
              <option value="">Tümü</option>
              {['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </label>
          <label>
            Durum
            <select name="durum" className="mon-input" value={filtre.durum} onChange={alanDegis}>
              <option value="">Tümü</option>
              <option value="2xx">2xx başarılı</option>
              <option value="3xx">3xx</option>
              <option value="4xx">4xx istemci hatası</option>
              <option value="5xx">5xx sunucu hatası</option>
              <option value="hata">Tüm hatalar (≥400)</option>
              <option value="401">401 yetkisiz</option>
              <option value="403">403 yasak</option>
              <option value="404">404 bulunamadı</option>
              <option value="429">429 çok fazla istek</option>
            </select>
          </label>
          <label>
            Path içinde
            <input type="text" name="path" className="mon-input" placeholder="/api/auth" value={filtre.path} onChange={alanDegis} maxLength={200} />
          </label>
          <label>
            IP
            <input type="text" name="ip" className="mon-input" placeholder="ör. 85.105." value={filtre.ip} onChange={alanDegis} maxLength={60} />
          </label>
          <label>
            Kullanıcı
            <input type="text" name="kullanici" className="mon-input" placeholder="kullanıcı adı" value={filtre.kullanici} onChange={alanDegis} maxLength={100} />
          </label>
          <label>
            Oturum
            <select name="rol" className="mon-input" value={filtre.rol} onChange={alanDegis}>
              <option value="">Tümü</option>
              <option value="anonim">Oturumsuz (anonim)</option>
              <option value="admin">admin</option>
              <option value="mod">mod</option>
              <option value="sales">sales</option>
              <option value="tech-service">tech-service</option>
              <option value="tech-person">tech-person</option>
              <option value="sevkiyat">sevkiyat</option>
              <option value="bayi">bayi</option>
              <option value="review-app">review-app</option>
              <option value="superadmin">superadmin</option>
            </select>
          </label>
        </div>
        <div className="mon-filtre-aksiyon">
          <button type="button" className="mon-btn" onClick={temizle}>
            Temizle
          </button>
          <button type="submit" className="mon-btn mon-btn--primary">
            <i className="fas fa-filter" aria-hidden /> Filtrele
          </button>
        </div>
      </form>

      {error && (
        <div className="mon-alert mon-alert--err" role="alert">
          {error}
        </div>
      )}

      {/* Liste */}
      <section className="mon-panel">
        <div className="mon-panel-head">
          <h2>
            <i className="fas fa-stream" aria-hidden /> İstekler
            {toplam != null && <span className="mon-sayac">{toplam.toLocaleString('tr-TR')}</span>}
          </h2>
          {sonYenileme && <span className="mon-ipucu">Son yenileme: {zamanFormat(sonYenileme)}</span>}
        </div>

        {loading && kayitlar.length === 0 ? (
          <div className="mon-bos">
            <i className="fas fa-spinner fa-spin" aria-hidden /> Yükleniyor…
          </div>
        ) : kayitlar.length === 0 ? (
          <div className="mon-bos">Bu filtreye uyan istek yok.</div>
        ) : (
          <div className="mon-tablo-kap">
            <table className="mon-tablo">
              <thead>
                <tr>
                  <th>Zaman</th>
                  <th>Method</th>
                  <th>Path</th>
                  <th>Durum</th>
                  <th>Süre</th>
                  <th>IP</th>
                  <th>Kullanıcı</th>
                </tr>
              </thead>
              <tbody>
                {kayitlar.map((k) => {
                  const acik = acikId === k._id;
                  const xffFarkli = k.iddiaEdilenIp && k.ip && k.iddiaEdilenIp !== k.ip;
                  return (
                    <React.Fragment key={k._id}>
                      <tr
                        className={`mon-satir ${acik ? 'mon-satir--acik' : ''}`}
                        onClick={() => setAcikId(acik ? null : k._id)}
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setAcikId(acik ? null : k._id);
                          }
                        }}
                        aria-expanded={acik}
                      >
                        <td className="mon-nowrap">{zamanFormat(k.zaman)}</td>
                        <td>
                          <span className={`mon-method mon-method--${(k.method || '').toLowerCase()}`}>{k.method}</span>
                        </td>
                        <td className="mon-path" title={k.path}>
                          {k.path}
                        </td>
                        <td>
                          <span className={`mon-durum ${durumSinifi(k.durumKodu)}`}>
                            {k.durumKodu ?? (k.yarida ? 'iptal' : '—')}
                          </span>
                        </td>
                        <td className="mon-nowrap">{k.sureMs != null ? `${k.sureMs} ms` : '—'}</td>
                        <td className="mon-nowrap">
                          {k.ip || '—'}
                          {xffFarkli && (
                            <i
                              className="fas fa-exclamation-triangle mon-uyari"
                              title={`İstemci farklı bir IP bildirdi (X-Forwarded-For): ${k.iddiaEdilenIp}`}
                              aria-label="X-Forwarded-For farklı"
                            />
                          )}
                        </td>
                        <td className="mon-nowrap">
                          {k.kullaniciAdi ? (
                            <>
                              {k.kullaniciAdi} <span className="mon-rol">{k.rol}</span>
                            </>
                          ) : (
                            <span className="mon-anonim">anonim</span>
                          )}
                        </td>
                      </tr>
                      {acik && (
                        <tr className="mon-detay-satir">
                          <td colSpan={7}>
                            <div className="mon-detay">
                              <div className="mon-detay-meta">
                                <div>
                                  <b>Tam zaman:</b> {new Date(k.zaman).toLocaleString('tr-TR')}
                                </div>
                                <div>
                                  <b>IP:</b> {k.ip || '—'}
                                </div>
                                <div>
                                  <b>X-Forwarded-For:</b> {k.xff || '—'}
                                </div>
                                <div>
                                  <b>Bağlantı IP:</b> {k.soketIp || '—'}
                                </div>
                                <div>
                                  <b>User-Agent:</b> {k.userAgent || '—'}
                                </div>
                                <div>
                                  <b>Origin:</b> {k.origin || '—'}
                                </div>
                                <div>
                                  <b>Referer:</b> {k.referer || '—'}
                                </div>
                                <div>
                                  <b>İçerik tipi:</b> {k.icerikTipi || '—'}
                                </div>
                                <div>
                                  <b>Boyut:</b> istek {k.istekBoyutu ?? '—'} B · yanıt {k.yanitBoyutu ?? '—'} B
                                </div>
                              </div>
                              <div className="mon-detay-json">
                                <div>
                                  <h4>Query</h4>
                                  <pre>{jsonGoster(k.query)}</pre>
                                </div>
                                <div>
                                  <h4>Body</h4>
                                  <pre>{jsonGoster(k.body)}</pre>
                                </div>
                              </div>
                              <div className="mon-detay-aksiyon">
                                {k.ip && (
                                  <button type="button" className="mon-btn mon-btn--sm" onClick={() => ipFiltrele(k.ip)}>
                                    Bu IP'nin tüm istekleri
                                  </button>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="mon-sayfalama">
          <button type="button" className="mon-btn mon-btn--sm" disabled={sayfa <= 1 || loading} onClick={() => setSayfa((p) => p - 1)}>
            ‹ Önceki
          </button>
          <span>
            Sayfa {sayfa}
            {toplamSayfa != null ? ` / ${toplamSayfa}` : ''}
          </span>
          <button type="button" className="mon-btn mon-btn--sm" disabled={!sonrakiVar || loading} onClick={() => setSayfa((p) => p + 1)}>
            Sonraki ›
          </button>
        </div>
      </section>
    </div>
  );
};

export default MonitorPage;
